import React, { memo } from 'react';
import { areEqual } from 'Utils/equalityChecks';
import type { Pipeline, ProjectStageConfig } from '../types';

import classes from './jobConfigList.module.css';

interface JobConfigListProps {
  pipelines: Pipeline[];
  configs: ProjectStageConfig[];
  onSelect: (pipeline: Pipeline) => void;
}

function JobConfigList({ pipelines, configs, onSelect }: JobConfigListProps) {
  if (pipelines.length === 0) {
    return (
      <div className={classes.emptyState}>
        <p className="text-muted mb-0">
          No pipelines found. Create a pipeline in your CRM first.
        </p>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {pipelines.map((pipeline) => {
        const pipelineConfigs = configs.filter((c) => c.pipeline_id === pipeline.id);
        const primaryConfig = pipelineConfigs[0];

        return (
          <button
            key={pipeline.id}
            type="button"
            className={classes.pipelineRow}
            onClick={() => onSelect(pipeline)}
          >
            <div className={classes.pipelineInfo}>
              <span className={classes.pipelineName}>{pipeline.name}</span>
              {primaryConfig ? (
                <div className={classes.configSummary}>
                  <span className="text-muted">
                    {`Trigger: ${primaryConfig.trigger_stage_name || primaryConfig.trigger_stage_id}`}
                  </span>
                  <span className="text-muted mx-1">&middot;</span>
                  <span className="text-muted">
                    {`Status: ${primaryConfig.project_status_name || primaryConfig.project_status_id}`}
                  </span>
                  {primaryConfig.company_name && (
                    <>
                      <span className="text-muted mx-1">&middot;</span>
                      <span className="text-muted">
                        {primaryConfig.company_name}
                      </span>
                    </>
                  )}
                  <span className="text-muted mx-1">&middot;</span>
                  <span className="text-muted">
                    {`${primaryConfig.field_mappings?.length || 0} field mapping${(primaryConfig.field_mappings?.length || 0) !== 1 ? 's' : ''}`}
                  </span>
                  {pipelineConfigs.length > 1 && (
                    <>
                      <span className="text-muted mx-1">&middot;</span>
                      <span className="text-muted">
                        {`+${pipelineConfigs.length - 1} stage mapping${pipelineConfigs.length - 1 !== 1 ? 's' : ''}`}
                      </span>
                    </>
                  )}
                </div>
              ) : (
                <span className={classes.configSummaryMuted}>Not configured</span>
              )}
            </div>
            <div className={classes.pipelineRight}>
              <span className={`badge ${primaryConfig ? 'bg-success' : 'bg-secondary bg-opacity-25 text-muted'}`}>
                {primaryConfig ? 'Configured' : 'Not Configured'}
              </span>
              <span className={classes.chevron}>&rsaquo;</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

const JobConfigListMemo = memo(JobConfigList, areEqual);

export { JobConfigListMemo as JobConfigList };
